/**
 * Lecture de l'historique d'usage pour le HUD : libellés des jours, phrase
 * qui décrit un quart d'heure d'une installation, épisodes de bouchon.
 */
import {
  CLOSED,
  bucketLevel,
  bucketRange,
  calendarDay,
  findJams,
  seriesValue,
  usageBucketAt,
  minuteOfDay,
  dayIndex,
  type FacilityKind,
  type FacilityMeta,
  type FacilitySeries,
  type Jam,
  type UsageDay,
  type UsageHistory,
} from '@snowchain/shared';
import { levelLabel } from './labels';
import { n0 } from './format';

/** « Aujourd'hui », « Hier » ou « sam. 12 déc. ». */
export function dayLabel(day: number, today: number): string {
  if (day === today) return "Aujourd'hui";
  if (day === today - 1) return 'Hier';
  return calendarDay(day).label;
}

/** Quart d'heure en cours (-1 hors plage mesurée). */
export function currentBucket(gameTime: number): number {
  return usageBucketAt(minuteOfDay(gameTime));
}

export function todayIndex(gameTime: number): number {
  return dayIndex(gameTime);
}

/** Journée détaillée `day` (null : la plus récente). */
export function usageDay(h: UsageHistory | null | undefined, day: number | null): UsageDay | undefined {
  if (!h) return undefined;
  if (day === null) return h.days[h.days.length - 1];
  return h.days.find((d) => d.day === day);
}

/** Attente en secondes de simulation : « 1 min 20 », « 45 s ». */
export function waitText(sec: number): string {
  if (sec >= 3600) return '> 1 h';
  if (sec >= 60) {
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return s >= 10 ? `${m} min ${s}` : `${m} min`;
  }
  return `${n0(sec)} s`;
}

const SERVED_WORD: Record<FacilityKind, string> = {
  accueil: 'entrées',
  ticket: 'forfaits',
  rental: 'paires louées',
  food: 'repas',
  lift: 'passages',
  piste: 'descentes',
  stock: '',
};

const LOST_WORD: Record<FacilityKind, string> = {
  accueil: 'refusés',
  ticket: 'repartis',
  rental: 'clients perdus',
  food: 'refoulés',
  lift: '',
  piste: '',
  stock: '',
};

/** Phrase d'un quart d'heure : « 9 h 15 à 9 h 30 · Saturé · 96 % · file 14 · attente 1 min 20 · 6 clients perdus ». */
export function describeBucket(meta: FacilityMeta, s: FacilitySeries | undefined, i: number): string {
  const parts = [bucketRange(i)];
  const lv = bucketLevel(meta.kind, s, i);
  if (lv === null) return `${parts[0]} · pas de mesure`;
  if (meta.kind === 'stock') {
    return `${parts[0]} · denrées ${n0(seriesValue(s, 'food', i))} % · carburant ${n0(seriesValue(s, 'fuel', i))} % · eau ${n0(seriesValue(s, 'water', i))} %`;
  }
  parts.push(levelLabel(lv));
  if (lv !== CLOSED) parts.push(`${n0(Math.max(0, seriesValue(s, 'u', i)))} % ${meta.kind === 'accueil' ? 'du parking' : meta.kind === 'food' ? 'des places' : meta.kind === 'piste' ? 'du confort' : 'du débit'}`);
  const q = seriesValue(s, 'q', i);
  if (q > 0) parts.push(`file ${n0(q)}`);
  const w = seriesValue(s, 'w', i);
  if (w > 0) parts.push(`attente ${waitText(w)}`);
  const n = seriesValue(s, 'n', i);
  if (n > 0 && SERVED_WORD[meta.kind]) parts.push(`${n0(n)} ${SERVED_WORD[meta.kind]}`);
  const x = seriesValue(s, 'x', i);
  if (x > 0 && LOST_WORD[meta.kind]) parts.push(`${n0(x)} ${LOST_WORD[meta.kind]}`);
  const crowd = seriesValue(s, 'crowd', i);
  if (crowd > 0) parts.push(`${n0(crowd)} présents`);
  const off = seriesValue(s, 'off', i);
  if (off > 0 && lv !== CLOSED) parts.push(`${n0(off)} min hors service`);
  return parts.join(' · ');
}

export interface DayJam extends Jam {
  meta: FacilityMeta;
}

/** Bouchons d'une journée, toutes installations confondues, du plus grave au moins grave. */
export function dayJams(day: UsageDay | undefined): DayJam[] {
  if (!day) return [];
  const out: DayJam[] = [];
  for (const meta of day.facilities) {
    if (meta.kind === 'stock') continue;
    for (const j of findJams(meta.kind, day.series[meta.key], day.n)) out.push({ ...j, meta });
  }
  return out.sort((a, b) => b.level - a.level || b.lost - a.lost || b.end - b.start - (a.end - a.start) || b.maxW - a.maxW);
}

/** Phrase d'un épisode : « attente max 4 min · 23 clients perdus · pic 100 % ». */
export function describeJam(j: Jam, kind: FacilityKind): string {
  const parts: string[] = [];
  if (j.maxW > 0) parts.push(`attente max ${waitText(j.maxW)}`);
  if (j.maxQ > 0) parts.push(`file max ${n0(j.maxQ)}`);
  if (j.lost > 0 && LOST_WORD[kind]) parts.push(`${n0(j.lost)} ${LOST_WORD[kind]}`);
  parts.push(`pic ${n0(j.peakU)} %`);
  return parts.join(' · ');
}

/** Somme d'une métrique sur la journée (valeurs absentes ignorées). */
export function seriesSum(s: FacilitySeries | undefined, metric: 'n' | 'x', n: number): number {
  let sum = 0;
  for (let i = 0; i < n; i++) sum += Math.max(0, seriesValue(s, metric, i));
  return sum;
}

export function seriesMax(s: FacilitySeries | undefined, metric: 'u' | 'w' | 'q' | 'crowd', n: number): number {
  let m = 0;
  for (let i = 0; i < n; i++) m = Math.max(m, seriesValue(s, metric, i));
  return m;
}

/** Temps saturé court pour une case de journée : vide, « 45 » (minutes) ou « 2h ». */
export function jamShort(minutes: number): string {
  if (minutes <= 0) return '';
  return minutes >= 60 ? `${Math.round(minutes / 60)}h` : String(minutes);
}
